<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const props = defineProps<{
  initialData?: { name: string; description: string | null }
  loading?: boolean
  errors?: Record<string, string[]>
}>()

const emit = defineEmits<{
  submit: [payload: { name: string; description: string | null }]
}>()

const { t } = useI18n()

const name = ref(props.initialData?.name ?? '')
const description = ref(props.initialData?.description ?? '')

watch(
  () => props.initialData,
  (data) => {
    if (data) {
      name.value = data.name
      description.value = data.description ?? ''
    }
  },
)

function handleSubmit(): void {
  emit('submit', {
    name: name.value,
    description: description.value.trim() === '' ? null : description.value,
  })
}
</script>

<template>
  <form novalidate @submit.prevent="handleSubmit">
    <div class="space-y-5">
      <div>
        <label for="wf-name" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('wizard_forms.form.name_label') }}
          <span class="text-red-400">*</span>
        </label>
        <input
          id="wf-name"
          v-model="name"
          type="text"
          :placeholder="t('wizard_forms.form.name_placeholder')"
          class="w-full rounded-lg bg-slate-700/50 border px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          :class="errors?.name ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500'"
        />
        <p v-if="errors?.name" class="mt-1.5 text-xs text-red-400">{{ errors.name[0] }}</p>
      </div>

      <div>
        <label for="wf-description" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('wizard_forms.form.description_label') }}
        </label>
        <textarea
          id="wf-description"
          v-model="description"
          rows="4"
          :placeholder="t('wizard_forms.form.description_placeholder')"
          class="w-full rounded-lg bg-slate-700/50 border px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors resize-none"
          :class="errors?.description ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500'"
        />
        <p v-if="errors?.description" class="mt-1.5 text-xs text-red-400">{{ errors.description[0] }}</p>
      </div>

      <button
        type="submit"
        :disabled="loading"
        class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
      >
        <AppSpinner v-if="loading" size="button" />
        <span>{{ loading ? t('common.loading') : t('common.save') }}</span>
      </button>
    </div>
  </form>
</template>
